import React from 'react';
import { Button, Progress, Space } from 'antd';
import { CloseOutlined, PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';

export type DownloadStatus = 'downloading' | 'paused' | 'completed' | 'error';

interface FileDownload {
  id: string;
  name: string;
  url: string;
  progress: number;
  status: DownloadStatus;
}

interface DownloadProgressPanelProps {
  files: FileDownload[];
  onCancel: () => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
}

const DownloadProgressPanel: React.FC<DownloadProgressPanelProps> = ({
  files,
  onCancel,
  onPause,
  onResume
}) => {
  const allComplete = files.every(f => f.progress === 100 && f.status === 'completed');

  if (files.length === 0) return null;

  const getStatusColor = (status: DownloadStatus) => {
    switch (status) {
      case 'downloading': return '#108ee9';
      case 'paused': return '#faad14';
      case 'completed': return '#87d068';
      case 'error': return '#f5222d';
      default: return '#108ee9';
    }
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-96 max-h-[500px] overflow-auto
                 bg-[var(--neutral-950)] border border-[#26324a] rounded-lg shadow-xl
                 p-4 transition-all duration-300"
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-[#F5F6FA]">
          {allComplete ? 'Download Complete' : 'Downloads'}
        </h3>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onCancel}
          className="!text-slate-400 hover:!text-white"
          disabled={allComplete}
        />
      </div>

      <div className="space-y-3">
        {files.map(file => {
          const isDownloading = file.status === 'downloading';
          const isPaused = file.status === 'paused';
          const isCompleted = file.status === 'completed';
          const isError = file.status === 'error';

          return (
            <div key={file.id} className="text-sm bg-[var(--neutral-900)] p-3 rounded">
              <div className="flex justify-between items-start mb-2">
                <span className="text-slate-300 truncate flex-1 mr-2 leading-tight">
                  {file.name}
                </span>
                <Space size={4}>
                  {isDownloading && onPause && (
                    <Button
                      type="text"
                      size="small"
                      icon={<PauseOutlined />}
                      onClick={() => onPause(file.id)}
                      className="!text-slate-300 hover:!text-white !p-1"
                    />
                  )}
                  {isPaused && onResume && (
                    <Button
                      type="text"
                      size="small"
                      icon={<PlayCircleOutlined />}
                      onClick={() => onResume(file.id)}
                      className="!text-slate-300 hover:!text-white !p-1"
                    />
                  )}
                  {isError && (
                    <span className="text-red-400 text-xs">Error</span>
                  )}
                </Space>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Progress
                  percent={file.progress}
                  showInfo={false}
                  strokeColor={getStatusColor(file.status)}
                  trailColor="var(--neutral-800)"
                  size="small"
                />
                <span className="text-cyan-400 font-mono text-xs w-12 text-right">
                  {file.progress}%
                </span>
              </div>
              {isPaused && (
                <div className="text-xs text-amber-400">Paused</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DownloadProgressPanel;
